import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/reducers';
import { actions } from '../store/actions';
import { inspectionService } from '../services/inspection.service';
import { showSuccessMessage } from '../helper/showMessage';

// Global polling state to prevent multiple instances
let globalPollingInterval: number | null = null;
let isPollingActive = false;
let pollingComponents = 0;
let lastSuccessMessageShown = false;

export const useInspectionPolling = () => {
    const dispatch = useDispatch();
    const { currentInspection, previousInspection, isPolling } = useSelector((state: RootState) => state.inspection);

    // Use refs to avoid dependency issues
    const currentInspectionRef = useRef(currentInspection);
    const previousInspectionRef = useRef(previousInspection);

    // Update refs when state changes
    useEffect(() => {
        currentInspectionRef.current = currentInspection;
    }, [currentInspection]);

    useEffect(() => {
        previousInspectionRef.current = previousInspection;
    }, [previousInspection]);

    // Function to fetch current in-progress inspection
    const fetchInProgressInspection = useCallback(async () => {
        try {
            const inspection = await inspectionService.getLastInProgressAndFailed();

            // Check for status changes and show appropriate messages
            if (previousInspectionRef.current &&
                previousInspectionRef.current.status === 'in-progress' &&
                !inspection &&
                !lastSuccessMessageShown) {
                showSuccessMessage("Inspection completed successfully, no faults detected");
                lastSuccessMessageShown = true;
            }

            // Reset success message flag when a new inspection starts
            if (inspection && inspection.status === 'in-progress') {
                lastSuccessMessageShown = false;
            }

            // Only show report popup when inspection status changes from 'in-progress' to 'failed'
            if (previousInspectionRef.current && previousInspectionRef.current.status === 'in-progress' && inspection && inspection.status === 'failed') {
                // Inspection just failed, show the report
                console.log(`Inspection status changed to failed - showing report`);
                dispatch(actions.setCompletedInspection(inspection));
            }

            // Update the previous inspection reference
            dispatch(actions.updatePreviousInspection(currentInspectionRef.current));

            // Always update with the latest inspection data
            dispatch(actions.setCurrentInspection(inspection));

            // Start polling only if there's an in-progress inspection
            if (inspection && inspection.status === 'in-progress' && !isPollingActive) {
                console.log('In-progress inspection detected - starting polling');
                isPollingActive = true;

                // Start polling
                globalPollingInterval = window.setInterval(() => {
                    fetchInProgressInspection();
                }, 2000); // Poll every 2 seconds

                dispatch(actions.startPolling(globalPollingInterval));
            }

            // Stop polling if no in-progress inspection
            if ((!inspection || inspection.status !== 'in-progress') && isPollingActive && globalPollingInterval) {
                console.log('No in-progress inspection - stopping polling');
                clearInterval(globalPollingInterval);
                globalPollingInterval = null;
                isPollingActive = false;
                dispatch(actions.stopPolling());
            }

            // Fetch checklist data if there's a current inspection (in-progress or failed)
            if (inspection && (inspection.status === 'in-progress' || inspection.status === 'failed')) {
                try {
                    const checklists = await inspectionService.getChecklistsByInspection(inspection.id);
                    dispatch(actions.setCurrentChecklists(checklists));

                    // Check if there are any failed checklist items (but don't auto-open modal)
                    const hasFailedItems = checklists.some(checklist => checklist.status === 'failed');
                    if (hasFailedItems && inspection.status === 'failed') {
                        console.log('Critical issue detected for inspection:', inspection.id);
                        dispatch(actions.setCompletedInspection(inspection));
                        // Modal opening is now handled outside polling - no automatic opening
                    }
                } catch (err: any) {
                    console.error('Failed to fetch checklists:', err);
                }
            } else {
                dispatch(actions.setCurrentChecklists([]));
                dispatch(actions.setHasCriticalEvent(false));
            }

        } catch (err: any) {
            console.error('Failed to fetch in-progress inspection:', err);
        }
    }, [dispatch]);

    // Effect to start polling when component mounts
    useEffect(() => {
        // Increment component count
        pollingComponents++;

        // Initial fetch to check for active inspection
        fetchInProgressInspection();

        return () => {
            // Decrement component count
            pollingComponents--;

            // Only stop polling if no components are using it
            if (pollingComponents === 0 && isPollingActive && globalPollingInterval) {
                clearInterval(globalPollingInterval);
                globalPollingInterval = null;
                isPollingActive = false;
                dispatch(actions.stopPolling());
            }
        };
    }, []); // Empty dependency array to run only once

    // Function to immediately check for inspection updates (called when new inspection is created)
    const checkInspectionImmediately = useCallback(async () => {
        await fetchInProgressInspection();
    }, [fetchInProgressInspection]);

    return {
        fetchInProgressInspection,
        checkInspectionImmediately,
        isPolling
    };
};
